/**
 * Tennis Schedule Viewer — renders fixtures synced from api-tennis.com.
 *
 * Subscribes to `tennisMatches` for the league's men's and women's tournament
 * keys. Live updates: when an admin presses "DO TENNIS API CALL" in another
 * tab and matches change, this view reflects them without a manual refresh.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { TennisMatch } from '../../types/database';
import { tennisMatchService } from '../../services/firestore';

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function playerLogo(m: TennisMatch, side: 'first' | 'second'): string | undefined {
  // Prefer the top-level field (populated by sync). Fall back to the raw event
  // object for matches synced before the field was added to the shape.
  if (side === 'first') {
    if (m.firstPlayerLogo) return m.firstPlayerLogo;
    const raw = m.raw as Record<string, unknown> | undefined;
    const v = raw?.event_first_player_logo;
    return typeof v === 'string' && v ? v : undefined;
  }
  if (m.secondPlayerLogo) return m.secondPlayerLogo;
  const raw = m.raw as Record<string, unknown> | undefined;
  const v = raw?.event_second_player_logo;
  return typeof v === 'string' && v ? v : undefined;
}

type Props = {
  mensTournamentKey?: number;
  womensTournamentKey?: number;
};

type Gender = 'men' | 'women';

function formatSetScore(scores: TennisMatch['scores']): string {
  if (!scores || scores.length === 0) return '—';
  return scores
    .map((s) => {
      const f = s.scoreFirst;
      const sd = s.scoreSecond;
      // Tiebreak strings come as "7.7" / "6.4" → render as "7-6(4)" if we can parse the dot
      const parseTb = (v: string) => {
        if (typeof v !== 'string' || !v.includes('.')) return null;
        const [games, tb] = v.split('.');
        return { games, tb };
      };
      const fTb = parseTb(f);
      const sTb = parseTb(sd);
      if (fTb && sTb) {
        // The losing tiebreak score is on the player who lost the set.
        // "7.7" / "6.5" means winner had 7 games + tiebreak score 7, loser had 6 games + tiebreak 5
        const winnerGames = fTb.games > sTb.games ? fTb.games : sTb.games;
        const loserGames = fTb.games > sTb.games ? sTb.games : fTb.games;
        const loserTb = fTb.games > sTb.games ? sTb.tb : fTb.tb;
        return `${winnerGames}-${loserGames}(${loserTb})`;
      }
      return `${f}-${sd}`;
    })
    .join(', ');
}

function abbreviateRound(round: string | null | undefined): string {
  if (!round) return '';
  const r = round.toLowerCase();
  if (r.includes('final') && r.includes('semi')) return 'SF';
  if (r.includes('quarter')) return 'QF';
  if (r.includes('r128')) return 'R128';
  if (r.includes('r64')) return 'R64';
  if (r.includes('r32')) return 'R32';
  if (r.includes('r16')) return 'R16';
  if (r.includes('final')) return 'F';
  return round.split(' - ').pop() ?? round;
}

function groupByDate(matches: TennisMatch[]): Map<string, TennisMatch[]> {
  const m = new Map<string, TennisMatch[]>();
  for (const match of matches) {
    const key = match.eventDate || 'Unscheduled';
    if (!m.has(key)) m.set(key, []);
    m.get(key)!.push(match);
  }
  return m;
}

function describeSubscriptionError(err: Error): string {
  const msg = err.message || String(err);
  if (/index/i.test(msg) && /building/i.test(msg)) {
    return 'Firestore index is still building. This usually takes 1–5 minutes after the first sync. Refresh in a moment.';
  }
  if (/index/i.test(msg)) {
    return 'Firestore is missing a composite index for this query. Run firebase deploy --only firestore:indexes.';
  }
  if (/permission/i.test(msg) || /insufficient/i.test(msg)) {
    return 'Missing Firestore read permission for /tennis/data/matches. The Firestore rules likely need a redeploy.';
  }
  return `Failed to load fixtures: ${msg}`;
}

const TennisScheduleViewer: React.FC<Props> = ({ mensTournamentKey, womensTournamentKey }) => {
  const [tab, setTab] = useState<Gender>('men');
  const [mensMatches, setMensMatches] = useState<TennisMatch[]>([]);
  const [womensMatches, setWomensMatches] = useState<TennisMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const unsubs: Array<() => void> = [];
    let mensReady = !mensTournamentKey;
    let womensReady = !womensTournamentKey;
    const markReady = () => { if (mensReady && womensReady) setLoading(false); };
    const handleError = (err: Error) => {
      // eslint-disable-next-line no-console
      console.error('TennisScheduleViewer subscription error:', err);
      setError(describeSubscriptionError(err));
      setLoading(false);
    };

    if (mensTournamentKey) {
      const unsub = tennisMatchService.subscribeByTournament(
        mensTournamentKey,
        (ms) => {
          setMensMatches(ms);
          mensReady = true;
          markReady();
        },
        handleError
      );
      unsubs.push(unsub);
    }
    if (womensTournamentKey) {
      const unsub = tennisMatchService.subscribeByTournament(
        womensTournamentKey,
        (ms) => {
          setWomensMatches(ms);
          womensReady = true;
          markReady();
        },
        handleError
      );
      unsubs.push(unsub);
    }
    if (!mensTournamentKey && !womensTournamentKey) setLoading(false);

    return () => { unsubs.forEach((u) => u()); };
  }, [mensTournamentKey, womensTournamentKey]);

  const visibleMatches = tab === 'men' ? mensMatches : womensMatches;
  const grouped = useMemo(() => groupByDate(visibleMatches), [visibleMatches]);
  const dates = useMemo(() => Array.from(grouped.keys()).sort(), [grouped]);

  // Auto-select best default date when matches or tab changes
  useEffect(() => {
    if (dates.length === 0) { setSelectedDate(null); return; }
    const todayYMD = new Date().toISOString().slice(0, 10);
    const defaultDate =
      dates.find(d => d >= todayYMD) ??
      dates[dates.length - 1] ??
      null;
    setSelectedDate(defaultDate);
  }, [dates]);

  if (!mensTournamentKey && !womensTournamentKey) {
    return (
      <Alert severity="info">
        This tennis league has no tournament keys configured yet. An admin needs to set
        the men&apos;s and women&apos;s tournament keys in the league settings.
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert severity="warning">
        {error}
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const todayYMD = new Date().toISOString().slice(0, 10);
  const displayMatches = selectedDate ? (grouped.get(selectedDate) ?? []) : [];

  const selectedDateLabel = selectedDate
    ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      })
    : '';

  return (
    <Stack spacing={2}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
        <Tab
          label={`Men's Draw${mensMatches.length ? ` (${mensMatches.length})` : ''}`}
          value="men"
          disabled={!mensTournamentKey}
        />
        <Tab
          label={`Women's Draw${womensMatches.length ? ` (${womensMatches.length})` : ''}`}
          value="women"
          disabled={!womensTournamentKey}
        />
      </Tabs>

      {visibleMatches.length === 0 ? (
        <Alert severity="info">
          No fixtures synced yet for this draw. An admin needs to press
          &quot;DO TENNIS API CALL&quot; on the Admin → Tennis Results tab.
        </Alert>
      ) : (
        <>
          {/* Date pill strip */}
          <Box sx={{
            display: 'flex',
            gap: 0.75,
            overflowX: 'auto',
            py: 0.5,
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}>
            {dates.map(date => {
              const d = new Date(date + 'T00:00:00');
              const label = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
              const isSelected = date === selectedDate;
              const isPast = date < todayYMD;
              return (
                <Chip
                  key={date}
                  label={label}
                  onClick={() => setSelectedDate(date)}
                  size="small"
                  variant={isSelected ? 'filled' : 'outlined'}
                  color={isSelected ? 'primary' : 'default'}
                  sx={{
                    flexShrink: 0,
                    opacity: isPast && !isSelected ? 0.6 : 1,
                    fontWeight: isSelected ? 700 : 400,
                    cursor: 'pointer',
                  }}
                />
              );
            })}
          </Box>

          {/* Date header */}
          {selectedDateLabel && (
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {selectedDateLabel}
            </Typography>
          )}

          {/* Match cards */}
          <Stack spacing={1.5}>
            {displayMatches.map((m) => {
              const winnerName = m.winner === 'First Player' ? m.firstPlayerName : m.winner === 'Second Player' ? m.secondPlayerName : null;
              const roundAbbr = abbreviateRound(m.round);
              const isFinished = m.status === 'Finished';

              return (
                <Box
                  key={m.eventKey}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.08)',
                    bgcolor: 'rgba(255,255,255,0.03)',
                    overflow: 'hidden',
                    position: 'relative',
                    '&::before': isFinished ? {
                      content: '""',
                      position: 'absolute',
                      top: 0, left: 0, right: 0,
                      height: '2px',
                      background: 'linear-gradient(90deg, transparent, rgba(102,187,106,0.6), transparent)',
                    } : {},
                  }}
                >
                  {/* Round badge row */}
                  {(roundAbbr || (m.status && !isFinished)) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, pt: 1, pb: 0.5 }}>
                      {roundAbbr && (
                        <Chip
                          label={roundAbbr}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            bgcolor: alpha('#1E88E5', 0.12),
                            color: alpha('#42A5F5', 0.9),
                            border: `1px solid ${alpha('#1E88E5', 0.25)}`,
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      )}
                      {m.status && !isFinished && (
                        <Chip
                          label={m.status}
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.75 } }}
                        />
                      )}
                    </Box>
                  )}

                  {/* Players row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1 }}>
                    {/* First player */}
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Avatar
                        src={playerLogo(m, 'first')}
                        alt={m.firstPlayerName}
                        sx={{ width: 32, height: 32, fontSize: '0.7rem', flexShrink: 0 }}
                      >
                        {initialsOf(m.firstPlayerName)}
                      </Avatar>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: winnerName === m.firstPlayerName ? 700 : 500,
                          color: winnerName === m.firstPlayerName ? '#fff' : 'rgba(255,255,255,0.7)',
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        noWrap
                      >
                        {winnerName === m.firstPlayerName && '🏆 '}
                        {m.firstPlayerName}
                      </Typography>
                    </Box>

                    {/* Score / time center */}
                    <Box sx={{ flexShrink: 0, textAlign: 'center', minWidth: 80 }}>
                      {isFinished ? (
                        <Typography
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            color: '#42A5F5',
                            fontWeight: 600,
                            letterSpacing: '0.02em',
                          }}
                        >
                          {formatSetScore(m.scores)}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {m.eventTime || 'TBD'}
                        </Typography>
                      )}
                    </Box>

                    {/* Second player */}
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, justifyContent: 'flex-end' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: winnerName === m.secondPlayerName ? 700 : 500,
                          color: winnerName === m.secondPlayerName ? '#fff' : 'rgba(255,255,255,0.7)',
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          textAlign: 'right',
                        }}
                        noWrap
                      >
                        {m.secondPlayerName}
                        {winnerName === m.secondPlayerName && ' 🏆'}
                      </Typography>
                      <Avatar
                        src={playerLogo(m, 'second')}
                        alt={m.secondPlayerName}
                        sx={{ width: 32, height: 32, fontSize: '0.7rem', flexShrink: 0 }}
                      >
                        {initialsOf(m.secondPlayerName)}
                      </Avatar>
                    </Box>
                  </Box>
                </Box>
              );
            })}

            {displayMatches.length === 0 && selectedDate && (
              <Alert severity="info">No matches on this date.</Alert>
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default TennisScheduleViewer;
